import { useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import {
  closeOverlay,
  openOverlay,
  type RunHistoryOverlayData,
} from '@/features/adp/state/overlaySlice'
import { usePipelineHistoryQuery } from '@/features/adp/queries/usePipelineQueries'
import type { PipelineHistoryItem } from '@/features/adp/services/pipeline/pipeline.types'
import Pagination from '@/shared/components/Pagination/Pagination'
import { cn } from '@/shared/lib/utils'
import { formatDateTime } from '@/shared/utils/formatDate'
import { Button } from '@/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { Input } from '@/ui/input'
import { MaterialIcon } from '@/ui/material-icon'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/table'
import { useQueryClient } from '@tanstack/react-query'
import styles from './RunHistoryModal.module.scss'

const TRIGGER_OPTIONS = [
  { value: 'all', label: 'All Triggers' },
  { value: 'immediate', label: 'Immediate' },
  { value: 'scheduled', label: 'Scheduled' },
]

type StatusKey =
  | 'finished'
  | 'failed'
  | 'stopped'
  | 'running'
  | 'initializing'
  | 'other'

function classifyStatus(status: string): StatusKey {
  const s = (status || '').toLowerCase().trim()
  if (s === 'finished' || s === 'success' || s === 'completed') return 'finished'
  if (s === 'failed' || s === 'error') return 'failed'
  if (s === 'stopped' || s === 'killed' || s === 'cancelled') return 'stopped'
  if (s === 'running' || s === 'in_progress') return 'running'
  if (s === 'initializing' || s === 'queued') return 'initializing'
  return 'other'
}

function StatusBadge({ status }: Readonly<{ status: string }>) {
  const key = classifyStatus(status)
  const label = status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'
  switch (key) {
    case 'finished':
      return (
        <span className={`${styles.statusPill} ${styles.statusFinished}`}>
          <MaterialIcon name="check" size={14} />
          {label}
        </span>
      )
    case 'failed':
      return (
        <span className={`${styles.statusPill} ${styles.statusFailed}`}>
          <MaterialIcon name="error" size={14} />
          {label}
        </span>
      )
    case 'stopped':
      return (
        <span className={`${styles.statusPill} ${styles.statusStopped}`}>
          <MaterialIcon name="stop" size={14} />
          {label}
        </span>
      )
    case 'running':
      return (
        <span className={`${styles.statusPill} ${styles.statusInProgress}`}>
          <MaterialIcon name="sync" size={14} />
          {label}
        </span>
      )
    case 'initializing':
      return (
        <span className={`${styles.statusPill} ${styles.statusInitializing}`}>
          <MaterialIcon name="schedule" size={14} />
          {label}
        </span>
      )
    default:
      return (
        <span className={`${styles.statusPill} ${styles.statusDefault}`}>
          {label}
        </span>
      )
  }
}

function formatTriggerLabel(value: string): string {
  if (!value) return '-'
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function getRunIdentifier(item: PipelineHistoryItem, fallbackIndex: number): string {
  if (item.run_id) return item.run_id
  if (item.start_time) return item.start_time
  return String(fallbackIndex)
}

const RunHistoryModal = () => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const overlay = useAppSelector((state) => state.overlay)

  // The overlay slice stack lets a child overlay (e.g. Logs) push History
  // onto the stack via openOverlay. We want History to stay visually
  // rendered behind Logs, so we treat ourselves as visible if we are the
  // current overlay OR if a frame for us exists on the stack — but only
  // while the overlay system as a whole is still open.
  const ownFrame = (() => {
    if (!overlay.isOpen) return null
    if (overlay.type === 'run-history-modal') {
      return overlay.data && 'dataFlowId' in overlay.data
        ? (overlay.data as RunHistoryOverlayData)
        : null
    }
    const stacked = overlay.stack.find((f) => f.type === 'run-history-modal')
    return stacked && stacked.data && 'dataFlowId' in stacked.data
      ? (stacked.data as RunHistoryOverlayData)
      : null
  })()

  const isVisible = ownFrame !== null
  const modalData: RunHistoryOverlayData | null = ownFrame

  const dataFlowId = modalData?.dataFlowId ?? 0
  const pipelineName = modalData?.pipelineName ?? ''

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [triggerFilter, setTriggerFilter] = useState('all')
  const [runCycle, setRunCycle] = useState('all')

  const queryParams = useMemo(
    () => ({ data_flow_id: dataFlowId, page, limit: pageSize }),
    [dataFlowId, page, pageSize]
  )

  const { data: historyData, isLoading, isError, error, isFetching } = usePipelineHistoryQuery(
    queryParams,
    { enabled: isVisible && Boolean(dataFlowId) }
  )

  const totalCount = historyData?.pagination?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const displayPage = Math.min(page, totalPages)

  const filteredRows = useMemo(() => {
    const rows = historyData?.history ?? []
    const term = search.trim().toLowerCase()
    return rows.filter((row) => {
      if (triggerFilter !== 'all' && (row.trigger_type || '').toLowerCase() !== triggerFilter) {
        return false
      }
      if (!term) return true
      const haystack = [row.status, row.trigger_type, row.start_time, row.end_time]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(term)
    })
  }, [historyData?.history, search, triggerFilter])

  const handleClose = () => {
    setPage(1)
    setSearch('')
    setTriggerFilter('all')
    setRunCycle('all')
    queryClient.removeQueries({ queryKey: ['pipeline-history'] })
    dispatch(closeOverlay())
  }

  const handleRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['pipeline-history'] })
  }

  const handleViewLogs = (row: PipelineHistoryItem, idx: number) => {
    const runId = getRunIdentifier(row, idx)
    dispatch(
      openOverlay({
        type: 'logs-modal',
        data: {
          pipelineName,
          dataFlowId,
          runId,
          startTime: row.start_time,
          endTime: row.end_time,
        },
      })
    )
  }

  let bodyContent: React.ReactNode
  if (isLoading) {
    bodyContent = (
      <TableRow>
        <TableCell colSpan={6} className={styles.loadingCell}>
          Loading run history…
        </TableCell>
      </TableRow>
    )
  } else if (isError) {
    bodyContent = (
      <TableRow>
        <TableCell colSpan={6} className={styles.errorCell}>
          Error loading history: {error?.message}
        </TableCell>
      </TableRow>
    )
  } else if (filteredRows.length === 0) {
    bodyContent = (
      <TableRow>
        <TableCell colSpan={6} className={styles.emptyCell}>
          No runs found.
        </TableCell>
      </TableRow>
    )
  } else {
    bodyContent = filteredRows.map((row, idx) => {
      const activityName = `${historyData?.data_flow_name ?? pipelineName}_Run_${
        (displayPage - 1) * pageSize + idx + 1
      }`
      return (
        <TableRow
          key={`${row.start_time ?? 'row'}-${idx}`}
          className={idx % 2 === 1 ? styles.rowAlt : undefined}
        >
          <TableCell className={styles.cellStrong}>{activityName}</TableCell>
          <TableCell className={styles.cellCapitalize}>
            {formatTriggerLabel(row.trigger_type)}
          </TableCell>
          <TableCell className={styles.statusCell}>
            <StatusBadge status={row.status} />
          </TableCell>
          <TableCell className={styles.cellMuted}>
            {row.start_time ? formatDateTime(row.start_time) : '-'}
          </TableCell>
          <TableCell className={styles.cellMuted}>
            {row.end_time ? formatDateTime(row.end_time) : '-'}
          </TableCell>
          <TableCell>
            <button
              type="button"
              className={styles.viewBtn}
              aria-label={`View logs for ${activityName}`}
              onClick={() => handleViewLogs(row, idx)}
            >
              <MaterialIcon name="terminal" size={20} />
              <span>Logs</span>
            </button>
          </TableCell>
        </TableRow>
      )
    })
  }

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <DialogContent
        className={cn(
          'max-w-[1120px] w-[95vw] h-[80vh] max-h-[80vh] p-0 flex flex-col',
          styles.dialogContent
        )}
      >
        <DialogHeader className={styles.header}>
          <div className={styles.headerRow}>
            <div className={styles.titleBlock}>
              <DialogTitle className={styles.title}>Run History</DialogTitle>
              <p className={styles.subtitle}>
                View and track the status of the activities for the pipeline/job
              </p>
            </div>
            <div className={styles.toolbar}>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={styles.iconCircleBtn}
                aria-label="Refresh run history"
                onClick={handleRefresh}
                disabled={isFetching}
              >
                <MaterialIcon name="refresh" size={20} />
              </Button>
              <div className={styles.searchWrap}>
                <span className={styles.searchIcon} aria-hidden>
                  <MaterialIcon name="search" size={18} />
                </span>
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by keyword"
                  className={styles.searchInput}
                  aria-label="Search run history by keyword"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" className={styles.filtersBtn}>
                    <MaterialIcon name="add" size={18} />
                    Filters
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={styles.filterDropdownMenu}>
                  <div className={styles.filterDropdownHeader}>
                    <MaterialIcon name="filter_list" size={16} />
                    <span>Filter by Trigger</span>
                  </div>
                  {TRIGGER_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      className={`${styles.filterDropdownItem} ${
                        triggerFilter === option.value ? styles.filterDropdownItemActive : ''
                      }`}
                      onSelect={() => {
                        setTriggerFilter(option.value)
                        setPage(1)
                      }}
                    >
                      <span>{option.label}</span>
                      {triggerFilter === option.value && <MaterialIcon name="check" size={16} />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Select value={runCycle} onValueChange={setRunCycle}>
                <SelectTrigger
                  className={styles.cycleSelect}
                  aria-label="Run cycle date range"
                  iconName="unfold_more"
                >
                  <SelectValue placeholder="Run Cycle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="last-7">Last 7 days</SelectItem>
                  <SelectItem value="last-30">Last 30 days</SelectItem>
                  <SelectItem value="last-90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <button
                type="button"
                className={styles.closeBtn}
                aria-label="Close run history"
                onClick={handleClose}
              >
                <MaterialIcon name="close" size={20} />
              </button>
            </div>
          </div>
        </DialogHeader>

        <div className={styles.body}>
          <div className={styles.tableScroll}>
            <Table className={styles.dataTable}>
              <TableHeader>
                <TableRow className={styles.headerRowTable}>
                  <TableHead className={styles.colActivity}>Activity Name</TableHead>
                  <TableHead className={styles.colTrigger}>Trigger Type</TableHead>
                  <TableHead className={styles.colStatus}>Status</TableHead>
                  <TableHead className={styles.colTime}>Start Time</TableHead>
                  <TableHead className={styles.colTime}>End Time</TableHead>
                  <TableHead className={styles.colAction}>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{bodyContent}</TableBody>
            </Table>
          </div>
        </div>

        <div className={styles.footer}>
          <Pagination
            currentPage={displayPage}
            totalPages={totalPages}
            rowsPerPage={pageSize}
            onPageChange={setPage}
            onRowsPerPageChange={(n) => {
              setPageSize(n)
              setPage(1)
            }}
            pageSizeOptions={[10, 25, 50]}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default RunHistoryModal
